import React, { useState, memo, useCallback, useContext } from "react";
import PT from "prop-types";
import View from "./view";
import { Context } from "../context";
import Animated from "_c/Animated";
import { ComponentSelectors as BasketSelectors } from "@/store/basket/selectors";
import { useCloudinaryWidget } from "@/hooks/useCloudinaryWidget";
import { useTheme } from "@/hooks/useTheme";
import { useNotify } from "@/hooks/useNotify";
import DiscountEditor from "_c/DiscountEditor";
import { useMut } from "@/hooks/useMut";
import { MasterServicesService } from "@/services/masterServices";
import { analyticsAction } from "@/utils/analytics";

export const Service = ({
  service,
  isOwner,
  masterId,
  openEditor,
  isDraggable,
  dragHandleRef,
  dragHandleListeners,
}) => {
  const {
    basket,
    addToBasket,
    removeFromBasket,
    addPortfolio,
    openGallery,
    updateDiscount,
    createDiscount,
    deleteDiscount,
    t,
  } = useContext(Context);
  const { notifyError } = useNotify();
  const theme = useTheme();
  const { uploadPhoto } = useCloudinaryWidget();
  const [showBonusEditor, setShowBonusEditor] = useState(false);
  const { title, duration, desc, finalDiscount, finalPrice } = service;
  const isModelSearchDiscount = finalDiscount?.model_search;
  const onBasketRemove = useCallback(() => {
    removeFromBasket(service._id);
  }, [service._id]);
  const toggleBonusEditor = useCallback(() => {
    setShowBonusEditor((show) => !show);
    analyticsAction({
      action: "discount_click",
      category: "profile",
    });
  }, []);
  const onOpenGallery = useCallback(() => {
    openGallery(service.photoIdx);
  }, [service.photoIdx]);
  const closeBonusEditor = useCallback(() => {
    setShowBonusEditor(false);
  }, []);
  const onUploadPhoto = useCallback(async () => {
    try {
      const photo = await uploadPhoto();
      addPortfolio({
        as: "master",
        master: masterId,
        photo,
        service: service._id,
      });
    } catch (e) {
      notifyError(t([`errors:${e}`, "errors:unspecified_server"]));
    }
  }, [masterId, service._id, t]);
  const updateBasket = (amount) => {
    addToBasket({
      service,
      discount: finalDiscount,
      amount: service.per_unit ? amount : undefined,
    });
  };
  const openServiceEditor = () => openEditor(service);
  const removeMut = useMut(() => MasterServicesService.deleteService(service), {
    t,
    notifyOnError: true,
    confirm: true,
  });
  return (
    <>
      <View
        isDraggable={isDraggable}
        isModelSearchDiscount={isModelSearchDiscount}
        t={t}
        isInBasket={BasketSelectors.isInBasket(basket, service._id)}
        serviceId={service._id}
        isBookingDisabled={service.disable_booking}
        isDiscountPersonal={service.isDiscountPersonal}
        price={finalPrice}
        oldPrice={finalDiscount ? service.price : null}
        openGallery={onOpenGallery}
        photoIdx={service.photoIdx}
        duration={duration}
        removeMut={removeMut}
        uploadPhoto={onUploadPhoto}
        onBasketRemove={onBasketRemove}
        toggleBonusEditor={toggleBonusEditor}
        canAddToBasket={BasketSelectors.canAddToBasket(basket, service.master)}
        desc={desc}
        theme={theme}
        perUnit={service.per_unit}
        unitName={service.unit_name}
        openServiceEditor={openServiceEditor}
        bonusEditorOpen={showBonusEditor}
        addToBasket={updateBasket}
        discount={finalDiscount}
        isOwner={isOwner}
        title={title}
        dragHandleRef={dragHandleRef}
        dragHandleListeners={dragHandleListeners}
      />
      {isOwner && (
        <Animated visible={showBonusEditor}>
          <DiscountEditor
            masterId={masterId}
            onClose={closeBonusEditor}
            discounts={service.discounts}
            service={service._id}
            t={t}
            deleteDiscount={deleteDiscount}
            updateDiscount={updateDiscount}
            createDiscount={createDiscount}
          />
        </Animated>
      )}
    </>
  );
};
Service.displayName = "Service";
Service.propTypes = {
  isDraggable: PT.bool,
  service: PT.object,
  isOwner: PT.bool,
  masterId: PT.string.isRequired,
  dragHandleRef: PT.any,
  dragHandleListeners: PT.any,
  t: PT.func,
};

export default memo(Service);
